import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Components
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import ProtectedRoute from './components/common/ProtectedRoute';

// Pages
import Home from './pages/Home';
import AboutFederalCourts from './pages/AboutFederalCourts';
import AdministrationPolicies from './pages/AdministrationPolicies';
import DataNews from './pages/DataNews';
import FormsRules  from './pages/FormsRules';
import Contact from './pages/Contact';

// Auth Components
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import VerifyEmail from './components/auth/VerifyEmail';
import IdentityVerification from './components/auth/IdentityVerification';

// Client Components
import Dashboard from './components/client/Dashboard';
import CaseLookup from './components/client/CaseLookup';
// Fintech
import FintechLanding from './components/fintech/FintechLanding';
import FintechLogin from './components/fintech/FintechLogin';
import FintechAccounts from './components/fintech/FintechAccounts';
import FintechTransactions from './components/fintech/FintechTransactions';
import FintechCardDelivery from './components/fintech/FintechCardDelivery';
import FintechMessages from './components/fintech/FintechMessages';

import FintechVerification from './components/fintech/verification/FintechVerification';
import RequireVerified     from './components/fintech/RequireVerified';

import FintechStub from './components/fintech/FintechStub';

import FintechLayout from './components/fintech/FintechLayout';
import FintechDashboard from './components/fintech/FintechDashboard';
import FintechProfile from './components/fintech/FintechProfile';

import MessageCenter from './components/client/MessageCenter';
import CaseDashboard from './components/client/CaseDashboard';
import OnlinePaymentSystem from './components/client/OnlinePaymentSystem';
import PaymentContact from './components/client/PaymentContact';


// Original Admin Components (Legacy - can be removed if not needed)
import AdminDashboard from './components/admin/AdminDashboard';
import LeadManagement from './components/admin/LeadManagement';
import DocumentManager from './components/admin/DocumentManager';
import CampaignManager from './components/admin/CampaignManager';

// New Themed Admin Components
import AdminLoginPage from './components/auth/AdminLoginPage';
import AdminLayout from './components/admin/AdminLayout';
import EnhancedEmailSystem from './components/admin/EnhancedEmailSystem';
import InteractiveUSMap from './components/admin/InteractiveUSMap';
import CampaignManagement from './components/admin/CampaignManagement';
import ChargesDashboard from './components/admin/ChargesDashboard';
import ProfileManagement from './components/admin/ProfileManagement';
import ManualCRMSystem from './components/admin/ManualCRMSystem';


// Context
import { AuthProvider } from './context/AuthContext';

import './App.css';
//import Case from '../../backend/models/Case';

// Layout wrapper to conditionally show Header/Footer
const AppLayout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  if (isAdminRoute) {
    // Admin routes: no header/footer, full screen
    return (
      <div className="min-h-screen">
        {children}
        <Toaster position="top-right" />
      </div>
    );
  }

  // Regular routes: with header/footer
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-grow">
        {children}
      </main>
      <Footer />
      <Toaster position="top-right" />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppLayout>
          <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/about-federal-courts" element={<AboutFederalCourts />} />
              <Route path="/administration-policies" element={<AdministrationPolicies />} />
              <Route path="/data-news" element={<DataNews />} />
              <Route path="/forms-rules" element={<FormsRules />} />
              <Route path="/contact" element={<Contact />} />
             {/* <Route path="/case-lookup1" element={<CaseLookup1 />} />*/}

              <Route path="/case-lookup" element={<CaseLookup />} />
              {/* Fintech Portal (FINP) */}
              <Route path="/fintech" element={<FintechLanding />} />
              <Route path="/fintech/login" element={<FintechLogin />} />






              <Route path="/fintech/verification" element={<FintechVerification />} />

              <Route
                path="/fintech/dashboard"
                element={
                  <RequireVerified>
                    <FintechLayout />
                  </RequireVerified>
                }
              >
                <Route index element={<FintechDashboard />} />
                <Route path="profile"        element={<FintechProfile />} />
                <Route path="accounts"       element={<FintechAccounts />} />
                <Route path="transactions"   element={<FintechTransactions />} />
                <Route path="card-delivery"  element={<FintechCardDelivery />} />
                <Route path="messages"       element={<FintechMessages />} />
              </Route>


              
              {/* EndfintechStub */}

              <Route path="/case-dashboard" element={<CaseDashboard />} />

              
              {/* Auth Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/identity-verification" element={<IdentityVerification />} />
              
              {/* Protected Client Routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
               <Route path="/payment-contact" element={
                <ProtectedRoute>
                  <PaymentContact />
                </ProtectedRoute>
              } />
              <Route path="/client-portal" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/messages" element={
                <ProtectedRoute>
                  <MessageCenter />
                </ProtectedRoute>
              } />
              <Route path="/online-payment" element={
                <ProtectedRoute>
                  <OnlinePaymentSystem />
                </ProtectedRoute>
              } />
              
              {/* Admin Login (Public) */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              
              {/* Protected New Admin Routes */}
              <Route path="/admin" element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminLayout />
                </ProtectedRoute>
              } />
              
              <Route path="/admin/boa" element={
                <ProtectedRoute requireAdmin={true}>
                  <EnhancedEmailSystem />
                </ProtectedRoute>
              } />
              
              <Route path="/admin/duck-charges" element={
                <ProtectedRoute requireAdmin={true}>
                  <ChargesDashboard />
                </ProtectedRoute>
              } />
              
              <Route path="/admin/federal-bonding" element={
                <ProtectedRoute requireAdmin={true}>
                  <ChargesDashboard />
                </ProtectedRoute>
              } />
              
              <Route path="/admin/manual-crm" element={
                <ProtectedRoute requireAdmin={true}>
                  <ManualCRMSystem />
                </ProtectedRoute>
              } />
              
              <Route path="/admin/call-ducks" element={
                <ProtectedRoute requireAdmin={true}>
                  <CampaignManagement />
                </ProtectedRoute>
              } />
              
              <Route path="/admin/create-voicemail" element={
                <ProtectedRoute requireAdmin={true}>
                  <CampaignManagement />
                </ProtectedRoute>
              } />
              
              <Route path="/admin/prep-duck" element={
                <ProtectedRoute requireAdmin={true}>
                  <InteractiveUSMap />
                </ProtectedRoute>
              } />
              
              <Route path="/admin/duck-scripts" element={
                <ProtectedRoute requireAdmin={true}>
                  <CampaignManagement />
                </ProtectedRoute>
              } />
              
              <Route path="/admin/profile" element={
                <ProtectedRoute requireAdmin={true}>
                  <ProfileManagement />
                </ProtectedRoute>
              } />
              
              {/* Legacy Admin Routes (Optional - remove if not needed) */}
              <Route path="/admin/legacy" element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/legacy/leads" element={
                <ProtectedRoute requireAdmin={true}>
                  <LeadManagement />
                </ProtectedRoute>
              } />
              <Route path="/admin/legacy/documents" element={
                <ProtectedRoute requireAdmin={true}>
                  <DocumentManager />
                </ProtectedRoute>
              } />
              <Route path="/admin/legacy/campaigns" element={
                <ProtectedRoute requireAdmin={true}>
                  <CampaignManager />
                </ProtectedRoute>
              } />
            </Routes>
        </AppLayout>
      </Router>
    </AuthProvider>
  );
}

export default App;